import { useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import {
  FormSourceRef,
  FormSourceRefKind,
} from "@evergiving/common/dist/es2015/pledge";
import debug from "debug";
import { notNil } from "@evergiving/common/dist/es2015/typescriptUtils";

const log = debug(__filename);

function formSourceRefFromQueryParam(
  instance: string | string[] | undefined
): FormSourceRef | undefined {
  if (instance === undefined) {
    return undefined;
  }

  log("formSourceRefFromQueryParam, instance=", instance);

  const asArray = Array.isArray(instance) ? instance : [instance];
  const match = asArray
    .map((param) => param.match(/^([it])(\d+)$/))
    .find(notNil);
  if (!match) {
    log("formSourceRefFromQueryParam, no match");
    return undefined;
  }

  log(
    "formSourceRefFromQueryParam, match[1]=",
    match[1],
    " match[2]=",
    match[2]
  );

  return {
    kind:
      match[1] === "t"
        ? FormSourceRefKind.TeamMemberRef
        : FormSourceRefKind.DigitalFormInstanceRef,
    id: Number(match[2]),
  };
}

export function useFormRouter(): {
  goBack: () => void;
  goToPage: (page: string) => void;
  replaceWithPage: (page: string) => void;
  replaceWithLocale: (page: string) => void;
  prefetchPage: (page: string) => void;
  getCurrentLocale: () => string;
  getFormSourceRef: () => FormSourceRef | undefined;
} {
  const router = useRouter();

  const getCurrentLocale = () => String(router.query.locale);

  const getPageUrl = useCallback(
    (page: string) => {
      const { digitalFormId, locale } = router.query;
      const pathPrefix = (router.pathname.match("(^/preview)") || [""])[0];
      const suffix = page === "index" ? "" : `${page}/`;
      if (pathPrefix.length === 0) {
        // published or dev
        return `/${locale}/${suffix}`;
      }
      // preview
      return `${pathPrefix}/${digitalFormId}/${locale}/${suffix}`;
    },
    [router.pathname, router.query]
  );

  const goBack = useCallback(() => {
    router.back();
  }, [router]);

  const goToPage = useCallback(
    (page: string) => {
      router.push(getPageUrl(page));
    },
    [router, getPageUrl]
  );

  const replaceWithPage = useCallback(
    (page: string) => {
      router.replace(getPageUrl(page));
    },
    [router, getPageUrl]
  );

  const prefetchPage = useCallback(
    (page: string) => {
      router.prefetch(getPageUrl(page));
    },
    [router, getPageUrl]
  );

  const replaceWithLocale = useCallback(
    (locale: string) => {
      const { pathname } = router;
      router.replace(
        pathname
          .replace("[digitalFormId]", String(router.query.digitalFormId))
          .replace("[locale]", locale)
      );
    },
    [router]
  );

  const formSourceRef = useMemo(
    () => formSourceRefFromQueryParam(router.query.i),
    [router.query.i]
  );

  const getFormSourceRef = () => formSourceRef;

  return {
    goBack,
    goToPage,
    replaceWithPage,
    replaceWithLocale,
    prefetchPage,
    getCurrentLocale,
    getFormSourceRef,
  };
}
